import React, { useState, useEffect } from "react";
import MetaTags from "react-meta-tags";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge
} from "reactstrap";
import { withRouter, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import cookieHelper from "helpers/getCookieData";
import { getAuthUser, getUserRole, getUserPermissions, isAdminUser } from "helpers/permissionHelper";
import { getClinicSeoSlug } from "helpers/seoHelper";
import { showSuccessAlert, showErroeAlert } from "pages/utils/alertMessages";
import {
  RiUserLine,
  RiHospitalLine,
  RiLockPasswordLine,
  RiShieldKeyholeLine,
  RiCheckDoubleLine,
  RiMailLine,
  RiPhoneLine,
  RiStethoscopeLine,
  RiMapPinLine,
  RiCalendarEventLine,
  RiShieldCheckLine,
  RiKeyLine,
  RiSaveLine,
  RiTimeLine,
  RiAwardLine,
  RiExternalLinkLine,
  RiShareLine,
  RiCheckLine,
  RiToothLine
} from "react-icons/ri";

import "./user-profile.scss";

const UserProfile = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("personal");
  const [authUser, setAuthUser] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [userRole, setUserRole] = useState("admin");
  const [copied, setCopied] = useState(false);

  // Form States
  const [profileForm, setProfileForm] = useState({
    name: "",
    email: "",
    mobile: "",
    qualification: "",
    reg_number: "",
    specialization: ""
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  const [saving, setSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    // 1. Fetch Auth User from cookie / helper
    const user = getAuthUser();
    const role = getUserRole();
    if (user) {
      setAuthUser(user);
      setUserRole(role);

      const resolvedName = user.name ||
        user.user_name ||
        (user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : '') ||
        (role === 'admin' ? 'Clinic Administrator' : role === 'doctor' ? 'Practicing Doctor' : 'Staff Member');

      setProfileForm({
        name: resolvedName,
        email: user.email || "",
        mobile: user.mobile || user.phone || "",
        qualification: user.qualification || (role === 'doctor' ? "BDS, MDS (Dental Surgery)" : ""),
        reg_number: user.reg_number || user.code || (role === 'doctor' ? "DENT-REG-4901" : `EMP-${user.id || '101'}`),
        specialization: user.specialization || (role === 'doctor' ? "Senior Dental Surgeon" : role === 'admin' ? "Clinic Administration & Operations" : "Front Desk & Patient Care")
      });
    }

    // 2. Fetch Active Clinic details
    try {
      const clinicCookie = cookieHelper.getCookie('_c');
      if (clinicCookie) {
        setClinic(JSON.parse(atob(clinicCookie)));
      }
    } catch (e) {
      console.warn("Failed to parse clinic cookie", e);
    }
  }, []);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      showSuccessAlert(t("Success"), "Profile information updated successfully!");
    } catch (err) {
      showErroeAlert(t("Error"), "Failed to update profile info");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      showErroeAlert(t("Error"), "New password and confirmation password do not match!");
      return;
    }
    if (passwordForm.new_password.length < 6) {
      showErroeAlert(t("Error"), "Password must be at least 6 characters long!");
      return;
    }

    setPasswordSaving(true);
    try {
      showSuccessAlert(t("Success"), "Password updated successfully!");
      setPasswordForm({
        current_password: "",
        new_password: "",
        confirm_password: ""
      });
    } catch (err) {
      showErroeAlert(t("Error"), "Failed to change password");
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleCopyLink = () => {
    const slug = getClinicSeoSlug(clinic);
    const shareUrl = `${window.location.origin}/clinic/${slug}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const permissions = getUserPermissions();
  const clinicSlug = getClinicSeoSlug(clinic);

  return (
    <React.Fragment>
      <div className="page-content profile-page-wrapper">
        <MetaTags>
          <title>{t("Profile")} | {process.env.REACT_APP_TITLE}</title>
        </MetaTags>

        <Container fluid className="profile-container-custom">
          {/* Top Title Banner */}
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <div>
              <h4 className="font-weight-bold text-dark mb-1 font-size-18 d-flex align-items-center gap-2">
                <RiUserLine className="text-primary" /> {t("Profile")}
              </h4>
              <p className="text-muted mb-0 font-size-13">
                {t("Manage your practitioner identity, clinic profile, login credentials, and role permissions")}
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Link
                to={`/clinic/${clinicSlug}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-outline-primary rounded-pill px-3 font-size-12 d-inline-flex align-items-center gap-1.5 shadow-xs"
              >
                <RiHospitalLine /> {t("View Details")} <RiExternalLinkLine className="opacity-75" />
              </Link>
            </div>
          </div>

          <Row className="g-4">
            {/* Left Column: User Identity & Practice Card */}
            <Col lg={4}>
              <div className="profile-card overflow-hidden mb-4">
                <div className="profile-hero-header">
                  <div className="profile-avatar-circle">
                    {profileForm.name ? profileForm.name.replace('Dr. ', '').trim().charAt(0).toUpperCase() : "U"}
                  </div>
                  <h5 className="text-white font-weight-bold font-size-17 mb-1">
                    {profileForm.name || (userRole === 'admin' ? 'Clinic Administrator' : 'User Profile')}
                  </h5>
                  <div className="text-white-50 font-size-12 mb-2">
                    {profileForm.qualification || profileForm.specialization || (userRole === 'admin' ? 'Facility Management & Operations' : 'Clinical OPD')}
                  </div>
                  <div className="profile-role-tag">
                    {userRole === 'admin' ? 'ADMINISTRATOR • CLINIC OWNER' : userRole === 'doctor' ? 'DOCTOR • CLINICAL PRACTITIONER' : userRole === 'receptionist' ? 'RECEPTIONIST • FRONT DESK' : `${userRole.toUpperCase()} • STAFF`}
                  </div>
                </div>

                <div className="p-3 bg-white">
                  <div className="profile-info-row">
                    <span className="info-label"><RiMailLine className="text-primary" /> {t("Email Address")}</span>
                    <span className="info-val">{profileForm.email || "—"}</span>
                  </div>
                  <div className="profile-info-row">
                    <span className="info-label"><RiPhoneLine className="text-success" /> {t("Mobile Phone Number")}</span>
                    <span className="info-val">{profileForm.mobile || "—"}</span>
                  </div>
                  <div className="profile-info-row">
                    <span className="info-label"><RiAwardLine className="text-warning" /> {userRole === 'doctor' ? t("Registration No") || "Reg Number" : "Employee ID"}</span>
                    <span className="info-val">{profileForm.reg_number || "—"}</span>
                  </div>
                  <div className="profile-info-row">
                    <span className="info-label"><RiShieldCheckLine className="text-info" /> {t("Authentication")}</span>
                    <span className="badge bg-success-subtle text-success px-2 py-0.5 rounded-pill font-size-11">
                      {userRole === 'admin' ? 'Verified Administrator' : userRole === 'doctor' ? 'Verified Doctor' : 'Verified Staff'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Clinic Association Card */}
              <div className="profile-card p-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div className="bg-primary-subtle text-primary p-2 rounded-3">
                    <RiHospitalLine className="font-size-20" />
                  </div>
                  <div>
                    <h6 className="font-weight-bold text-dark mb-0 font-size-14">
                      {clinic?.name || clinic?.clinic_name || "909 Dental OPD & Implant Centre"}
                    </h6>
                    <small className="text-muted font-size-11">Active Practice Facility</small>
                  </div>
                </div>

                <div className="bg-light p-3 rounded-3 border mb-3 font-size-12">
                  <div className="d-flex justify-content-between mb-1.5">
                    <span className="text-muted">{userRole === 'doctor' ? t("Attending Doctor") : "Assigned Role"}:</span>
                    <Badge color="light" className="text-primary border font-size-11 px-2 text-uppercase">
                      {userRole === 'doctor' ? (clinic?.doctor_code || "DOC-01") : userRole}
                    </Badge>
                  </div>
                  <div className="d-flex justify-content-between mb-1.5">
                    <span className="text-muted">{t("Operating Hours") || "Time Slot"}:</span>
                    <strong className="text-dark">09:00 AM - 08:30 PM</strong>
                  </div>
                  <div className="border-top pt-1.5 mt-1.5">
                    <span className="text-muted d-block mb-0.5">{t("Address")}:</span>
                    <span className="text-dark font-size-11 d-flex align-items-start gap-1">
                      <RiMapPinLine className="text-danger flex-shrink-0 mt-0.5" />
                      {clinic?.address || "Health City Medical Arcade, Pune"}
                    </span>
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    color="light"
                    size="sm"
                    className="w-100 border font-size-12 d-flex align-items-center justify-content-center gap-1"
                    onClick={handleCopyLink}
                  >
                    {copied ? <RiCheckLine className="text-success" /> : <RiShareLine />}
                    {copied ? "Link Copied!" : "Share Clinic Link"}
                  </Button>
                  <Link
                    to={`/clinic/${clinicSlug}`}
                    target="_blank"
                    className="btn btn-sm btn-primary flex-shrink-0 px-2.5 font-size-12"
                    title="Open Clinic Public View"
                  >
                    <RiExternalLinkLine />
                  </Link>
                </div>
              </div>
            </Col>

            {/* Right Column: Tabbed Settings & Forms */}
            <Col lg={8}>
              <div className="profile-card overflow-hidden">
                <Nav className="profile-nav-pills">
                  <NavItem>
                    <NavLink
                      className={`cursor-pointer ${activeTab === "personal" ? "active" : ""}`}
                      onClick={() => setActiveTab("personal")}
                      style={{ cursor: "pointer" }}
                    >
                      <RiUserLine /> {t("Profile")}
                    </NavLink>
                  </NavItem>
                  {isAdminUser() && (
                    <NavItem>
                      <NavLink
                        className={`cursor-pointer ${activeTab === "clinic" ? "active" : ""}`}
                        onClick={() => setActiveTab("clinic")}
                        style={{ cursor: "pointer" }}
                      >
                        <RiHospitalLine /> {t("Settings")}
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      className={`cursor-pointer ${activeTab === "security" ? "active" : ""}`}
                      onClick={() => setActiveTab("security")}
                      style={{ cursor: "pointer" }}
                    >
                      <RiLockPasswordLine /> {t("Lock screen")}
                    </NavLink>
                  </NavItem>
                  {isAdminUser() && (
                    <NavItem>
                      <NavLink
                        className={`cursor-pointer ${activeTab === "permissions" ? "active" : ""}`}
                        onClick={() => setActiveTab("permissions")}
                        style={{ cursor: "pointer" }}
                      >
                        <RiShieldKeyholeLine /> {t("Settings & Roles")}
                      </NavLink>
                    </NavItem>
                  )}
                </Nav>

                <div className="p-4">
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="personal">
                      <div className="profile-section-heading">
                        <RiUserLine className="text-primary" /> {t("Profile")}
                      </div>
                      <span className="profile-section-sub">
                        {t("Manage your practitioner identity, clinic profile, login credentials, and role permissions")}
                      </span>

                      <form onSubmit={handleProfileSubmit}>
                        <Row className="g-3 mb-3">
                          <Col md={6}>
                            <div className="profile-field-group">
                              <label>{t("Full Name")} *</label>
                              <div className="profile-input-wrapper">
                                <RiUserLine className="input-icon" />
                                <input
                                  type="text"
                                  required
                                  className="form-control"
                                  placeholder="e.g. Full Name"
                                  value={profileForm.name}
                                  onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                                />
                              </div>
                            </div>
                          </Col>

                          <Col md={6}>
                            <div className="profile-field-group">
                              <label>{t("Email Address")} *</label>
                              <div className="profile-input-wrapper">
                                <RiMailLine className="input-icon" />
                                <input
                                  type="email"
                                  required
                                  className="form-control"
                                  placeholder="user@oralstop.com"
                                  value={profileForm.email}
                                  onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                                />
                              </div>
                            </div>
                          </Col>

                          <Col md={6}>
                            <div className="profile-field-group">
                              <label>{t("Mobile Phone Number")} *</label>
                              <div className="profile-input-wrapper">
                                <RiPhoneLine className="input-icon" />
                                <input
                                  type="tel"
                                  required
                                  className="form-control"
                                  placeholder="10-digit mobile number"
                                  value={profileForm.mobile}
                                  onChange={(e) => setProfileForm(prev => ({ ...prev, mobile: e.target.value }))}
                                />
                              </div>
                            </div>
                          </Col>

                          <Col md={6}>
                            <div className="profile-field-group">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <label className="mb-0">
                                  {userRole === 'doctor' ? (t("Registration No") || "Medical Registration No.") : "Employee / Staff ID"}
                                </label>
                                <span className="badge bg-light text-muted border font-size-10 py-0.5 px-1.5">
                                  <i className="bx bx-lock-alt me-0.5" /> Read-Only
                                </span>
                              </div>
                              <div className="profile-input-wrapper">
                                <RiAwardLine className="input-icon text-muted" />
                                <input
                                  type="text"
                                  readOnly
                                  disabled
                                  className="form-control bg-light text-muted cursor-not-allowed"
                                  placeholder={userRole === 'doctor' ? "e.g. DENT-MH-49201" : "e.g. EMP-101"}
                                  value={profileForm.reg_number}
                                />
                              </div>
                            </div>
                          </Col>

                          <Col md={6}>
                            <div className="profile-field-group">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <label className="mb-0">
                                  {userRole === 'doctor' ? (t("Qualification") || "Qualification / Degree") : "Designation"}
                                </label>
                                <span className="badge bg-light text-muted border font-size-10 py-0.5 px-1.5">
                                  <i className="bx bx-lock-alt me-0.5" /> Read-Only
                                </span>
                              </div>
                              <div className="profile-input-wrapper">
                                <RiStethoscopeLine className="input-icon text-muted" />
                                <input
                                  type="text"
                                  readOnly
                                  disabled
                                  className="form-control bg-light text-muted cursor-not-allowed"
                                  placeholder={userRole === 'doctor' ? "e.g. BDS, MDS" : "e.g. Operations Manager"}
                                  value={profileForm.qualification}
                                />
                              </div>
                            </div>
                          </Col>

                          <Col md={6}>
                            <div className="profile-field-group">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <label className="mb-0">
                                  {userRole === 'doctor' ? (t("Specialization") || "Clinical Specialization") : "Department / Specialization"}
                                </label>
                                <span className="badge bg-light text-muted border font-size-10 py-0.5 px-1.5">
                                  <i className="bx bx-lock-alt me-0.5" /> Read-Only
                                </span>
                              </div>
                              <div className="profile-input-wrapper">
                                <RiToothLine className="input-icon text-muted" />
                                <input
                                  type="text"
                                  readOnly
                                  disabled
                                  className="form-control bg-light text-muted cursor-not-allowed"
                                  placeholder={userRole === 'doctor' ? "e.g. Dental Surgeon" : "e.g. Front Desk & Billing"}
                                  value={profileForm.specialization}
                                />
                              </div>
                            </div>
                          </Col>
                        </Row>

                        <div className="d-flex justify-content-end pt-3 border-top">
                          <Button
                            color="primary"
                            type="submit"
                            disabled={saving}
                            className="px-4 py-2 font-weight-bold d-inline-flex align-items-center gap-1.5 rounded-3 shadow-xs font-size-13"
                          >
                            {saving ? <span className="spinner-border spinner-border-sm" /> : <RiSaveLine className="font-size-16" />}
                            {t("Save Changes")}
                          </Button>
                        </div>
                      </form>
                    </TabPane>

                    <TabPane tabId="clinic">
                      <div className="profile-section-heading">
                        <RiHospitalLine className="text-primary" /> Active Clinic & Practice Setup
                      </div>
                      <span className="profile-section-sub">
                        Overview of your linked clinic facility, chair operatories, and public microsites.
                      </span>

                      <Row className="g-3 mb-4">
                        <Col md={6}>
                          <div className="p-3 bg-light rounded-3 border">
                            <span className="text-muted font-size-11 font-weight-bold text-uppercase d-block mb-1">Clinic Name</span>
                            <strong className="text-dark font-size-14">{clinic?.clinic_name || "909 Dental OPD & Implant Centre"}</strong>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="p-3 bg-light rounded-3 border">
                            <span className="text-muted font-size-11 font-weight-bold text-uppercase d-block mb-1">Attending Doctor Code</span>
                            <Badge color="light" className="text-primary border font-size-12 px-2.5 py-1">
                              {clinic?.doctor_code || "DOC-01"}
                            </Badge>
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="p-3 bg-light rounded-3 border">
                            <span className="text-muted font-size-11 font-weight-bold text-uppercase d-block mb-1">Facility Address</span>
                            <span className="text-dark font-size-13 d-flex align-items-center gap-1.5">
                              <RiMapPinLine className="text-danger flex-shrink-0" />
                              {clinic?.address || "Suite 401, Health City Medical Arcade, Pune, Maharashtra 411001"}
                            </span>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="p-3 bg-light rounded-3 border">
                            <span className="text-muted font-size-11 font-weight-bold text-uppercase d-block mb-1">Clinic Operating Hours</span>
                            <span className="text-dark font-size-13 d-flex align-items-center gap-1.5">
                              <RiTimeLine className="text-primary" /> Mon - Sat: 9:00 AM - 8:30 PM
                            </span>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="p-3 bg-light rounded-3 border">
                            <span className="text-muted font-size-11 font-weight-bold text-uppercase d-block mb-1">Public Booking Microsite</span>
                            <a
                              href={`/clinic/${clinicSlug}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary font-weight-semibold font-size-13 d-inline-flex align-items-center gap-1"
                            >
                              /clinic/{clinicSlug} &rarr;
                            </a>
                          </div>
                        </Col>
                      </Row>
                    </TabPane>

                    {/* Tab 3: Security & Password */}
                    <TabPane tabId="security">
                      <div className="profile-section-heading">
                        <RiLockPasswordLine className="text-primary" /> {t("Lock screen")}
                      </div>
                      <span className="profile-section-sub">
                        {t("Manage your practitioner identity, clinic profile, login credentials, and role permissions")}
                      </span>

                      <form onSubmit={handlePasswordSubmit} style={{ maxWidth: "480px" }}>
                        <div className="profile-field-group">
                          <label>{t("Forget Password")}</label>
                          <div className="profile-input-wrapper">
                            <RiKeyLine className="input-icon" />
                            <input
                              type="password"
                              className="form-control"
                              placeholder="••••••••"
                              value={passwordForm.current_password}
                              onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                            />
                          </div>
                        </div>

                        <div className="profile-field-group">
                          <label>{t("New")} {t("Authentication")} *</label>
                          <div className="profile-input-wrapper">
                            <RiLockPasswordLine className="input-icon" />
                            <input
                              type="password"
                              required
                              className="form-control"
                              placeholder="Enter new password (min 6 chars)"
                              value={passwordForm.new_password}
                              onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                            />
                          </div>
                        </div>

                        <div className="profile-field-group">
                          <label>{t("Confirm")} *</label>
                          <div className="profile-input-wrapper">
                            <RiLockPasswordLine className="input-icon" />
                            <input
                              type="password"
                              required
                              className="form-control"
                              placeholder="Re-type new password"
                              value={passwordForm.confirm_password}
                              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                            />
                          </div>
                        </div>

                        <Button
                          color="primary"
                          type="submit"
                          disabled={passwordSaving}
                          className="px-4 py-2 font-weight-bold d-inline-flex align-items-center gap-1.5 rounded-3 shadow-xs font-size-13"
                        >
                          {passwordSaving ? <span className="spinner-border spinner-border-sm" /> : <RiKeyLine className="font-size-16" />}
                          {t("Save Changes")}
                        </Button>
                      </form>
                    </TabPane>

                    {/* Tab 4: Role & Privileges Matrix */}
                    <TabPane tabId="permissions">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                          <div className="profile-section-heading">
                            <RiShieldKeyholeLine className="text-primary" /> Active Role & Privileges Matrix
                          </div>
                          <span className="profile-section-sub mb-0">
                            Assigned permissions for role: <strong>{userRole.toUpperCase()}</strong>
                          </span>
                        </div>
                        <Badge color="primary" className="px-3 py-1 text-uppercase font-size-12">
                          {userRole}
                        </Badge>
                      </div>

                      <div className="table-responsive mt-3">
                        <table className="table table-hover align-middle mb-0 font-size-12">
                          <thead className="table-light">
                            <tr>
                              <th>Module Name</th>
                              <th className="text-center">Read / View</th>
                              <th className="text-center">Create / Add</th>
                              <th className="text-center">Edit / Update</th>
                              <th className="text-center">Delete</th>
                              <th className="text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[
                              "Patients Master EMR",
                              "Appointments & Scheduling",
                              "Multi-Chair Operatory",
                              "Dental Odontogram Charting",
                              "Treatment Procedures",
                              "Digital Prescriptions (Rx)",
                              "Accounts & Billing Invoices",
                              "Reports & Analytics",
                              "Clinic Roles & Settings"
                            ].map((modName, idx) => (
                              <tr key={idx}>
                                <td><strong>{modName}</strong></td>
                                <td className="text-center"><span className="text-success font-weight-bold">✓ Yes</span></td>
                                <td className="text-center">
                                  {userRole === "doctor" && modName.includes("Settings") ? <span className="text-muted">—</span> : <span className="text-success font-weight-bold">✓ Yes</span>}
                                </td>
                                <td className="text-center">
                                  {userRole === "doctor" && modName.includes("Settings") ? <span className="text-muted">—</span> : <span className="text-success font-weight-bold">✓ Yes</span>}
                                </td>
                                <td className="text-center">
                                  {userRole === "admin" ? <span className="text-success font-weight-bold">✓ Yes</span> : <span className="text-danger font-weight-semibold">🔒 Admin</span>}
                                </td>
                                <td className="text-center">
                                  <Badge color="success" className="font-size-11 px-2 py-0.5">Active</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </TabPane>
                  </TabContent>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(UserProfile);
